import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Language, LinkedIn, X, Instagram, Facebook } from "@mui/icons-material";
import { FieldWrapper, ChoiceToggle, StepActions, PageHeader, SectionHeader, Callout } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetLoanSocialMediaQuery,
  usePatchSocialMediaDataMutation,
  usePostSocialMediaMutation,
} from "@api/socialMediaApi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError } from "@interfaces/response";
import { WEP_PAGE } from "@utils/expreciones";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

function atLeastOneSocialTest(this: yup.TestContext) {
  const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
  if (has_social_media) {
    return linkedin || facebook || instagram || twitter ? true : false;
  }
  return true;
}

export const PresenciaDigital = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.presencia_digital.code);

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getSocialMedia] = useLazyGetLoanSocialMediaQuery();
  const [postSocialMedia] = usePostSocialMediaMutation();
  const [patchSocialMedia] = usePatchSocialMediaDataMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [digitalPresenceId, setDigitalPresenceId] = useState("");
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  const formik = useFormik({
    initialValues: {
      has_web_page: false,
      web_page: "",
      has_social_media: false,
      linkedin: "",
      facebook: "",
      instagram: "",
      twitter: "",
    },
    validationSchema: yup.object().shape({
      has_web_page: yup.boolean().required("Campo requerido"),
      web_page: yup.string().when("has_web_page", {
        is: true,
        then: (schema) => schema.required("Campo requerido").matches(WEP_PAGE, "URL no válida"),
      }),
      has_social_media: yup.boolean().required("Campo requerido"),
      linkedin: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
      facebook: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
      instagram: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
      twitter: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);

        const socialMediaData = {
          has_web_page: values.has_web_page,
          web_page: values.web_page || "",
          has_social_media: values.has_social_media,
          linkedin: values.linkedin || "",
          facebook: values.facebook || "",
          instagram: values.instagram || "",
          x_twitter: values.twitter || "",
        };

        if (digitalPresenceId) {
          await patchSocialMedia({
            client_details_id: clientDetails.id,
            social_media_id: digitalPresenceId,
            data: socialMediaData,
          }).unwrap();
        } else {
          await postSocialMedia({ client_details_id: clientDetails.id, data: socialMediaData }).unwrap();
        }

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus?.url || "");
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "PresenciaDigital", values: formik.values } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { setValues, validateForm } = formik;

  const fetchSocialMedia = useCallback(async () => {
    try {
      setIsLoadingInitial(true);
      const socialMedia = await getSocialMedia(clientDetails.id).unwrap();
      if (socialMedia?.id) {
        setDigitalPresenceId(socialMedia.id);
        await setValues({
          has_web_page: socialMedia.has_web_page || false,
          web_page: socialMedia.web_page || "",
          has_social_media: socialMedia.has_social_media || false,
          linkedin: socialMedia.linkedin || "",
          facebook: socialMedia.facebook || "",
          instagram: socialMedia.instagram || "",
          twitter: socialMedia.x_twitter || "",
        });
        await validateForm();
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la información de las redes sociales", error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [clientDetails.id, getSocialMedia, setValues, validateForm]);

  useEffect(() => {
    fetchSocialMedia();
  }, [fetchSocialMedia]);

  const isFieldDisabled = formik.isSubmitting || isLoadingInitial;

  const handleBack = () => {
    if (previousStatus) {
      navigate(previousStatus.url);
    }
  };

  const redesValues = [formik.values.linkedin, formik.values.facebook, formik.values.instagram, formik.values.twitter];
  const hasAtLeastOneRed = redesValues.some((v) => v.trim());
  const hasRedesError = Boolean(
    formik.values.has_social_media &&
    (formik.errors.linkedin || formik.errors.facebook || formik.errors.instagram || formik.errors.twitter),
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Presencia digital"
        title="Presencia digital de la empresa"
        subtitle="Conocer tus canales digitales nos ayuda a mejorar tu oferta personalizada."
      />

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader label="Página web" first />
        <FieldWrapper label="¿La empresa cuenta con página web?">
          <ChoiceToggle
            value={formik.values.has_web_page ? "si" : "no"}
            onChange={(v) => {
              const value = v === "si";
              formik.setFieldValue("has_web_page", value);
              if (!value) formik.setFieldValue("web_page", "");
              setTimeout(() => formik.validateForm(), 200);
            }}
            options={[
              { value: "si", label: "Sí" },
              { value: "no", label: "No" },
            ]}
            size="small"
          />
        </FieldWrapper>

        {formik.values.has_web_page && (
          <FieldWrapper label="URL del sitio" error={(formik.touched.web_page && formik.errors.web_page) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="web_page"
              value={formik.values.web_page}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="empresa.com"
              inputProps={{ maxLength: 255 }}
              disabled={isFieldDisabled}
              InputProps={{ startAdornment: <Language fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
            />
          </FieldWrapper>
        )}

        <SectionHeader label="Redes sociales" />
        <FieldWrapper label="¿La empresa cuenta con redes sociales?">
          <ChoiceToggle
            value={formik.values.has_social_media ? "si" : "no"}
            onChange={(v) => {
              const value = v === "si";
              formik.setFieldValue("has_social_media", value);
              if (!value) {
                formik.setFieldValue("linkedin", "");
                formik.setFieldValue("facebook", "");
                formik.setFieldValue("instagram", "");
                formik.setFieldValue("twitter", "");
              }
              setTimeout(() => formik.validateForm(), 200);
            }}
            options={[
              { value: "si", label: "Sí" },
              { value: "no", label: "No" },
            ]}
            size="small"
          />
        </FieldWrapper>

        {formik.values.has_social_media && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FieldWrapper label="LinkedIn" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="linkedin"
                value={formik.values.linkedin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="linkedin.com/company/empresa"
                inputProps={{ maxLength: 255 }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <LinkedIn fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>
            <FieldWrapper label="X / Twitter" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="twitter"
                value={formik.values.twitter}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="@empresa"
                inputProps={{ maxLength: 255 }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <X fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>
            <FieldWrapper label="Instagram" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="instagram"
                value={formik.values.instagram}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="@empresa"
                inputProps={{ maxLength: 255 }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <Instagram fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>
            <FieldWrapper label="Facebook" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="facebook"
                value={formik.values.facebook}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="facebook.com/empresa"
                inputProps={{ maxLength: 255 }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <Facebook fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>

            {hasRedesError && (
              <Callout tone="warn">
                {!hasAtLeastOneRed
                  ? "Debes completar al menos una red social con una URL válida"
                  : "Alguna de las URLs no es válida"}
              </Callout>
            )}
          </Box>
        )}
      </Paper>

      <StepActions onBack={handleBack} onNext={formik.submitForm} nextDisabled={!formik.isValid || isFieldDisabled} />
    </Box>
  );
};
